// interfaces
import { ApiResponse } from "@payment-client/interfaces/Api";
import { Result, Ok, Err } from "@swiggy-private/js-fp";

// constants
import { OOPS_SOMETHING_WENT_WRONG } from "@payment-client/constants/TextDict";

// internal dep
import {
    recordNREvent,
    GlobalHeaderState,
    SwgyHeaderKeysList,
    NEWRELIC_TABLES,
    fetchData,
    Headers,
} from "@swiggy-private/checkout-core";

// helpers
import { isEmpty } from "@swiggy-private/core-ui";

export interface ApiCallParams {
    apiHost: string;
    endpoint: string;
    searchParams?: { [key: string]: string };
    bodyParams?: { [key: string]: unknown };
    options?: RequestInit;
    extraNRData?: { [key: string]: unknown };
    timeout?: number;
    retry?: boolean;
    retryCount?: number;
    shouldShowNetworkError?: boolean;
    excludeDefaultHeaders?: boolean;
}
const DOWNSTREAM_TRIGGER_SOURCE = {
    API_SERVER: "api-server",
    WEB_APP: "web-app",
};

function recordSessionData<T>(response: ApiResponse<T>, url: string): void {
    const existingSid = GlobalHeaderState.getHeader(SwgyHeaderKeysList.sid) || "";
    const existingTid = GlobalHeaderState.getHeader(SwgyHeaderKeysList.tid) || "";

    // If response object is null, no need to update session data.
    if (!response) {
        return;
    }

    const { tid, sid } = response;

    if (!isEmpty(tid)) {
        // record NR event in case of change in tid
        if (existingTid !== tid) {
            recordNREvent({
                apiUrl: url,
                eventType: NEWRELIC_TABLES.CHECKOUT_WEB_CART_SESSION_RECORD,
                statusCode: response.statusCode ?? -1,
            });
        }
        GlobalHeaderState.setHeader(SwgyHeaderKeysList.tid, tid);
    }

    if (!isEmpty(sid)) {
        // record NR event in case of change in sid
        if (existingSid !== sid) {
            recordNREvent({
                apiUrl: url,
                eventType: NEWRELIC_TABLES.CHECKOUT_WEB_CART_SESSION_RECORD,
                statusCode: response.statusCode ?? -1,
                sid,
                existingSid,
            });
        }
        GlobalHeaderState.setHeader(SwgyHeaderKeysList.sid, sid);
    }
}

async function nrInterceptor<T>(
    request: { startTime: number; url: string; opts: RequestInit; responseSize?: number },
    response: ApiResponse<T>,
    apiRetriedCount?: number,
): Promise<ApiResponse<T>> {
    recordNREvent({
        url: request.url,
        eventType: NEWRELIC_TABLES.PAYMENT_WEB_EXTERNAL_API,
        statusCode: response.statusCode ?? -1,
        statusMessage: response.statusMessage ?? "",
        method: request.opts.method || "GET",
        responseTime: Date.now() - request.startTime,
        apiRetriedCount: apiRetriedCount ?? 0,
        responseSize: request.responseSize ? parseFloat((request.responseSize / 1024).toFixed(2)) : 0,
        mId: String((request?.opts?.headers as Headers)?.["m_id"]),
    });

    //add logic for updating tid and sid
    recordSessionData(response, request.url);

    return response;
}

async function nrDownstreamInterceptor<T>(
    request: { startTime: number; url: string; opts: RequestInit; responseSize?: number },
    response: T,
    apiRetriedCount?: number,
): Promise<T> {
    const typedResponse = response as T & ApiResponse<T>;
    recordNREvent({
        url: request.url,
        eventType: NEWRELIC_TABLES.PAYMENT_WEB_EXTERNAL_API,
        method: request.opts.method || "GET",
        responseTime: Date.now() - request.startTime,
        statusCode: typedResponse?.statusCode ?? "--",
        statusMessage: typedResponse?.statusMessage ?? "-",
        hasData: `${!isEmpty(typedResponse?.data)}`,
        apiRetriedCount: apiRetriedCount ?? 0,
        responseSize: request.responseSize ? parseFloat((request.responseSize / 1024).toFixed(2)) : 0,
        mId: String((request?.opts?.headers as Headers)?.["m_id"]),
    });
    return response;
}

export function nrErrorLog(
    request: { startTime: number; url: string },
    err: unknown,
    downStreamTriggerSource = "",
): void {
    recordNREvent({
        url: request.url,
        eventType: NEWRELIC_TABLES.PAYMENT_WEB_EXTERNAL_API_ERROR,
        responseTime: Date.now() - request.startTime,
        error: err instanceof Error ? err.name : JSON.stringify(err),
        errorMessage: err instanceof Error ? err.message : JSON.stringify(err),
        httpStatusCode: err instanceof Response ? err.status : "",
        downStreamTriggerSource,
    });
}

export function nrErrorPlaceOrderSwiggyOneBL(
    request: { url: string },
    options: RequestInit,
    params: ApiCallParams,
    headers: Record<string, unknown>,
) {
    recordNREvent({
        url: request.url,
        eventType: NEWRELIC_TABLES.PAYMENT_WEB_EXTERNAL_API_NETWORK_ERROR,
        params: (options && JSON.stringify(params)) || "",
        options: (options && JSON.stringify(options)) || "",
        headers: (headers && JSON.stringify(headers)) || "",
    });
}

export async function makeApiCall<T>(params: ApiCallParams): Promise<ApiResponse<T>> {
    const { response, startTime, logUrl, options, apiRetriedCount, responseSize } = await fetchData<ApiResponse<T>>(
        params,
        DOWNSTREAM_TRIGGER_SOURCE.API_SERVER,
    );
    return nrInterceptor({ startTime, url: logUrl, opts: options, responseSize }, response, apiRetriedCount);
}

/**
 * @deprecated Use makeDownstreamApiCall instead from checkout-core. This function will be removed in a future
 */
export async function makeDownstreamApiCall<T>(params: ApiCallParams): Promise<T> {
    const { response, startTime, logUrl, options, apiRetriedCount, responseSize } = await fetchData<T>(
        params,
        DOWNSTREAM_TRIGGER_SOURCE.WEB_APP,
    );
    return nrDownstreamInterceptor({ startTime, url: logUrl, opts: options, responseSize }, response, apiRetriedCount);
}

export function unwrapResponse<T>(response?: ApiResponse<T>): Result<T, string> {
    if (response?.statusCode === 0) {
        return Ok(response.data as T);
    }

    return Err(response?.statusMessage || OOPS_SOMETHING_WENT_WRONG);
}

export function unwrapCorporateResponse<T extends { functionalError?: { code: number; message: string } }>(
    response?: ApiResponse<T>,
): Result<T, string> {
    if (response?.data?.functionalError?.code === 0) {
        return Ok(response.data as T);
    }

    throw Err(response?.data?.functionalError?.message || OOPS_SOMETHING_WENT_WRONG);
}

export function unwrapResponseWithCode<T>(
    response?: ApiResponse<T>,
): Result<T, Pick<ApiResponse, "statusCode" | "statusMessage">> {
    if (response?.statusCode === 0) {
        return Ok(response.data as T);
    }

    return Err({
        statusCode: response?.statusCode || 1,
        statusMessage: response?.statusMessage || OOPS_SOMETHING_WENT_WRONG,
    });
}
